import { useTrans } from "@/i18n"
import {
	useProductionGraphContext,
	AvailableDatePeriods,
} from "./ProductionGraphContext"

// DateTime
import { DateTime } from "@/lib/dates"
import { dateFormat } from "@/constants/constants"

// UI
import { cn } from "@/lib/utils"
import {
	Popover,
	PopoverButton,
	PopoverGroup,
	PopoverPanel,
	Transition,
} from "@/lib/ui"
import { Button } from "@/components/Button"
import { ChevronDownIcon } from "@/lib/icons"
import { DatePicker } from "@/components/form-controls/DatePicker"
import {
	DatePickerDate,
	transformToDatePickerDate,
} from "@/components/form-controls/datePicker.utils"

// Animations
import { getTransitionPopoverProps } from "@/lib/animations"

/**
 * ProductionGraphActions
 * @returns
 */
export function ProductionGraphActions() {
	const t = useTrans("project")
	const { setGraphInterval, graphInterval } = useProductionGraphContext()

	return (
		<div className="mt-4 flex flex-col gap-4 md:flex-row lg:mt-0">
			{/** Date range picker */}
			<DateRangePicker />
			<div>
				<Button
					onClick={() => setGraphInterval("day")}
					data-testid="project:data.production.button.monthly"
					className={cn(
						"rounded-r-none",
						graphInterval === AvailableDatePeriods.DAY &&
							"relative z-10",
					)}
					variant={
						graphInterval === AvailableDatePeriods.DAY
							? "primary"
							: "transparent"
					}
				>
					{t("project:data.production.button.monthly")}
				</Button>
				<Button
					onClick={() => setGraphInterval("month")}
					className={cn(
						"rounded-l-none border-l-0",
						graphInterval === AvailableDatePeriods.MONTH &&
							"relative z-10",
					)}
					variant={
						graphInterval === AvailableDatePeriods.MONTH
							? "primary"
							: "transparent"
					}
				>
					{t("project:data.production.button.yearly")}
				</Button>
			</div>
		</div>
	)
}

/**
 * DateRangePicker
 * @param param0
 * @returns
 */
function DateRangePicker({ className }: { className?: string }) {
	// Context
	const { startTime, setStartTime, endTime, setEndTime } =
		useProductionGraphContext()

	return (
		<div className={cn("flex items-center gap-4", className)}>
			<PopoverGroup className="flex items-center divide-x divide-gray-200">
				<Popover className="relative z-20 inline-block text-left">
					<PopoverButton
						className={cn(
							"group inline-flex items-center justify-center rounded-md",
							"border border-gray-200 bg-white px-4 py-2 text-sm text-gray-500",
							"focus:ring-primary-500 hover:bg-gray-50 hover:text-gray-900 focus:ring-2 focus:outline-hidden",
						)}
					>
						<span className="mr-2">
							{DateTime.fromJSDate(startTime).toFormat(
								dateFormat,
							)}
						</span>

						<ChevronDownIcon
							className="ml-2 size-5 shrink-0 text-gray-400 group-hover:text-gray-500"
							aria-hidden="true"
						/>
					</PopoverButton>

					<Transition {...getTransitionPopoverProps()}>
						<PopoverPanel className="absolute left-0 mt-2 origin-top-left rounded-md bg-white p-4 shadow-2xl ring-1 ring-black/5 focus:outline-hidden">
							{({ close }) => (
								<div
									className="min-w-[300px]"
									data-testid="daterangepicker-from"
								>
									<DatePicker
										selectedDate={transformToDatePickerDate(
											startTime,
										)}
										onSelectDate={(
											date: DatePickerDate,
										) => {
											const newDate =
												DateTime.fromObject(date)
											setStartTime(newDate.toJSDate())
											close()
										}}
									/>
								</div>
							)}
						</PopoverPanel>
					</Transition>
				</Popover>
			</PopoverGroup>
			<span className="text-sm text-gray-500">to</span>
			<PopoverGroup className="flex items-center divide-x divide-gray-200">
				<Popover className="relative z-20 inline-block text-left">
					<PopoverButton
						className={cn(
							"group inline-flex items-center justify-center rounded-md",
							"border border-gray-200 bg-white px-4 py-2 text-sm text-gray-500",
							"focus:ring-primary-500 hover:bg-gray-50 hover:text-gray-900 focus:ring-2 focus:outline-hidden",
						)}
					>
						<span className="mr-2">
							{DateTime.fromJSDate(endTime).toFormat(dateFormat)}
						</span>
						<ChevronDownIcon
							className="ml-2 size-5 shrink-0 text-gray-400 group-hover:text-gray-500"
							aria-hidden="true"
						/>
					</PopoverButton>

					<Transition {...getTransitionPopoverProps()}>
						<PopoverPanel className="absolute left-0 mt-2 origin-top-left rounded-md bg-white p-4 shadow-2xl ring-1 ring-black/5 focus:outline-hidden">
							{({ close }) => (
								<div
									className="min-w-[300px]"
									data-testid="daterangepicker-to"
								>
									<DatePicker
										selectedDate={transformToDatePickerDate(
											endTime,
										)}
										onSelectDate={(
											date: DatePickerDate,
										) => {
											const newDate =
												DateTime.fromObject(date)
											setEndTime(newDate.toJSDate())
											close()
										}}
									/>
								</div>
							)}
						</PopoverPanel>
					</Transition>
				</Popover>
			</PopoverGroup>
		</div>
	)
}
